import React, {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { cn } from "../../lib/utils.js";
import { mergeRefs } from "../../primitives/index.js";
import { Tooltip, type TooltipProps } from "../Tooltip/index.js";

/* ---------------- TruncatedText ----------------
   A string that truncates to one line or clamps to two, and shows its full
   text in the design-system Tooltip only while the text is clipped.

   It always renders through Tooltip and only toggles Tooltip's `disabled`, so
   crossing the clip threshold never remounts the text element. The full text
   is already in the DOM, so the tooltip does not describe its child (no
   aria-describedby): a screen reader would read the text twice.

   The text is not focusable, so a keyboard user gets no tooltip; assistive
   technology reads the full text from the DOM. */

export interface TruncatedTextProps extends Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  "children"
> {
  /** The full text. It is also the tooltip's content. */
  children: string;
  /** 1 (default): one line with an ellipsis, as a block, so it can replace a
   *  div. 2: clamps to two lines. */
  lines?: 1 | 2;
  /** Where the tooltip opens (Tooltip's placement; default top). */
  placement?: TooltipProps["placement"];
}

/* Tooltip's measuring wrapper (relative inline-flex) becomes a flex box that
   can shrink and never grows past its parent. So the parent sets the
   truncation width, and the wrapper does not shrink-wrap the text. */
const WRAPPER_CLASS = "flex min-w-0 max-w-full";

const LINES_CLASS: Record<1 | 2, string> = {
  1: "block truncate",
  2: "line-clamp-2",
};

function isClipped(text: HTMLElement, lines: 1 | 2): boolean {
  return lines === 2
    ? text.scrollHeight > text.clientHeight
    : text.scrollWidth > text.clientWidth;
}

export const TruncatedText = forwardRef<HTMLSpanElement, TruncatedTextProps>(
  function TruncatedText(
    { children, lines = 1, placement, className, ...rest },
    forwardedRef,
  ) {
    const textRef = useRef<HTMLSpanElement>(null);
    const ref = useMemo(() => mergeRefs(textRef, forwardedRef), [forwardedRef]);
    const [clipped, setClipped] = useState(false);

    const measure = useCallback(() => {
      const text = textRef.current;
      if (text) setClipped(isClipped(text, lines));
    }, [lines]);

    // Before paint, on mount and whenever the text or the line count changes.
    useLayoutEffect(() => measure(), [measure, children]);

    // Again whenever the text box resizes (its parent grew or shrank).
    useEffect(() => {
      const text = textRef.current;
      if (!text || typeof ResizeObserver === "undefined") return;
      const observer = new ResizeObserver(measure);
      observer.observe(text);
      return () => observer.disconnect();
    }, [measure]);

    return (
      <Tooltip
        content={children}
        disabled={!clipped}
        placement={placement}
        describeChild={false}
        wrapperClassName={WRAPPER_CLASS}
      >
        <span {...rest} ref={ref} className={cn(LINES_CLASS[lines], className)}>
          {children}
        </span>
      </Tooltip>
    );
  },
);
